import React from "react";
import { ChevronRight } from "lucide-react";
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export interface ModalBreadcrumbHeaderProps {
  breadcrumbs?: string[];
  title?: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }> | React.ReactNode;
  iconColor?: string;
  className?: string;
}

export function ModalBreadcrumbHeader({
  breadcrumbs,
  title,
  description,
  icon: Icon,
  iconColor = "text-emerald-600",
  className,
}: ModalBreadcrumbHeaderProps) {
  // Ensure we at least have the current title in breadcrumbs if empty, and deduplicate consecutive identical crumbs
  const rawCrumbs =
    breadcrumbs && breadcrumbs.length > 0 ? breadcrumbs : title ? [title] : [];
  const effectiveCrumbs = rawCrumbs.filter((c, i) => i === 0 || c !== rawCrumbs[i - 1]);
  const effectiveTitle = title || effectiveCrumbs[effectiveCrumbs.length - 1] || "";

  return (
    <DialogHeader className={className}>
      {/* Breadcrumb Hierarchy */}
      {effectiveCrumbs.length > 1 && (
        <div data-testid="modal-breadcrumb-trail" className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500 flex-wrap mb-0.5">
          {effectiveCrumbs.map((crumb, idx) => {
            const isLast = idx === effectiveCrumbs.length - 1;
            return (
              <React.Fragment key={idx}>
                {idx > 0 && <ChevronRight className="w-3 h-3 text-slate-400 shrink-0" />}
                <span
                  className={cn(
                    "transition-colors",
                    isLast
                      ? "px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold border border-emerald-200/80 shadow-xs"
                      : "text-slate-500 hover:text-slate-700"
                  )}
                >
                  {crumb}
                </span>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Main Title & Icon */}
      <DialogTitle className="flex items-center gap-2">
        {Icon &&
          (React.isValidElement(Icon) ? (
            Icon
          ) : typeof Icon === "function" || typeof Icon === "object" ? (
            React.createElement(Icon as React.ComponentType<{ className?: string }>, {
              className: cn("w-5 h-5 shrink-0", iconColor),
            })
          ) : null)}
        <span>{effectiveTitle}</span>
      </DialogTitle>

      {/* Description */}
      {description && (
        <DialogDescription className="text-xs text-slate-500 font-normal">
          {description}
        </DialogDescription>
      )}
    </DialogHeader>
  );
}
